import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import QuestionCard from "./QuestionCard";
import DragDropTutorial from "./DragDropTutorial";

const StartQuiz = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const [quizDetails, setQuizDetails] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [rankedResponses, setRankedResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(true);

  const encryptedQuizId = localStorage.getItem("quizid");
  const encryptedParticipantEmail = localStorage.getItem("participantEmail");

  useEffect(() => {
    const fetchQuizDetails = async () => {
      try {
        const response = await axiosPrivate.post("/participate/start", {
          encryptedQuizId,
          encryptedParticipantEmail,
        });

        setQuizDetails(response.data);
        setErrMsg("");
      } catch (err) {
        handleRequestError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuizDetails();
  }, [axiosPrivate, encryptedQuizId, encryptedParticipantEmail]);

  const handleQuestionSelect = (questionId, orderedOptionIds) => {
    setRankedResponses(prev => ({
      ...prev,
      [currentQuestionIndex]: orderedOptionIds
    }));
  };

  const handleNextQuestion = () => {
    setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    window.scrollTo(0, 0);
  };

  const handlePreviousQuestion = () => {
    setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    try {
      const formattedResponses = quizDetails.questions.map((q, qIdx) => {
        const orderedIds = rankedResponses[qIdx];
        const currentOrder = orderedIds || q.options.map((_, i) => `opt-${i}`);

        return q.options.map((_, oIdx) => {
          const optId = `opt-${oIdx}`;
          const rankIndex = currentOrder.indexOf(optId);
          return 4 - rankIndex;
        });
      });

      await axiosPrivate.post("/participate/submit", {
        responses: formattedResponses,
        encryptedQuizId,
        encryptedParticipantEmail,
      });

      navigate("/quiz/thankyou");
    } catch (err) {
      handleRequestError(err);
    }
  };

  const handleRequestError = (err) => {
    if (err?.response?.data?.error) {
      if (
        err.response.data.error ===
        "Incomplete profile, please provide details."
      ) {
        navigate(
          `/quiz/updateparticipant?q=${encryptedQuizId}&p=${encryptedParticipantEmail}`
        );
      }
      setErrMsg(err.response.data.error);
    } else {
      setErrMsg("Something went wrong. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="fade-in" style={{ textAlign: "center", padding: "4rem" }}>
        <div className="spin" style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
        <p style={{ color: "var(--text-secondary)" }}>Loading assessment...</p>
      </div>
    );
  }

  // Show tutorial before the first question
  if (showTutorial) {
    return <DragDropTutorial onComplete={() => setShowTutorial(false)} />;
  }

  const currentQuestion = quizDetails?.questions[currentQuestionIndex];
  const questionData = currentQuestion ? {
    id: `q-${currentQuestionIndex}`,
    index: currentQuestionIndex,
    text: currentQuestion.questionText,
    type: 'rank-order',
    options: currentQuestion.options.map((opt, idx) => ({
      id: `opt-${idx}`,
      text: opt
    }))
  } : null;

  return (
    <div className="fade-in" style={{ maxWidth: "800px", margin: "0 auto", padding: "2rem 1rem" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem", textAlign: "center" }}>
        <h1
          style={{
            fontSize: "2rem",
            fontWeight: "700",
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, var(--primary-purple-light), var(--accent-cyan))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Psychometric Assessment
        </h1>
        {quizDetails && (
          <div style={{ display: "flex", justifyContent: "center", gap: "1rem", marginTop: "1rem" }}>
            <span style={{
              background: "rgba(124, 58, 237, 0.1)",
              color: "var(--primary-purple-light)",
              padding: "0.25rem 0.75rem",
              borderRadius: "9999px",
              fontSize: "0.875rem",
              fontWeight: "600"
            }}>
              Question {currentQuestionIndex + 1} of {quizDetails.questions.length}
            </span>
            <span style={{
              background: "rgba(6, 182, 212, 0.1)",
              color: "var(--accent-cyan)",
              padding: "0.25rem 0.75rem",
              borderRadius: "9999px",
              fontSize: "0.875rem",
              fontWeight: "600"
            }}>
              {Math.round(((currentQuestionIndex) / quizDetails.questions.length) * 100)}% Complete
            </span>
          </div>
        )}
      </div>

      {errMsg && <p className="errmsg">{errMsg}</p>}

      {questionData && (
        <div>
          <QuestionCard
            question={questionData}
            selectedOptionId={rankedResponses[currentQuestionIndex]}
            onSelect={handleQuestionSelect}
          />

          {/* Navigation Buttons */}
          <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
            <button
              type="button"
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="btn-secondary"
              style={{ flex: 1, opacity: currentQuestionIndex === 0 ? 0.5 : 1 }}
            >
              Previous
            </button>

            {currentQuestionIndex === quizDetails.questions.length - 1 ? (
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Submit Assessment
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNextQuestion}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Next
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StartQuiz;
